import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useSelector } from "react-redux";

const FunnelChartComponent = () => {

  const pid = useSelector((state) => state.user.projectId);

  const [orgOptions, setOrgOptions] = useState([]);
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  const [caseDistrictOptions, setCaseDistrictOptions] = useState([]);

  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const [selectedCaseDistrict, setSelectedCaseDistrict] = useState([]);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [appliedFilters, setAppliedFilters] = useState({
    orgs: [],
    schemes: [],
    schemeTypes: [],
    caseDistricts: [],
    startDate: '',
    endDate: ''
  });

  const [actualTiles, setActualTiles] = useState([]);
  const [netTiles, setNetTiles] = useState([]);
  const [loading, setLoading] = useState(false);

  // State for info popups
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  const schemeTypeLabelMap = {
    sch: "Schemes",
    doc: "Document",
    dfl: "DFL",
    yc: "Yojana Card"
  };

  // Definitions with examples for different elements
  const definitions = {
    "Work Done in Period": {
      title: "Work Done in Period",
      description: "Work done represents the actual activities on ground completed during the selected period. The counts represent how many new applications were opened in selected period, how many applications (new & old) were submitted, and how many (new & old) moved to benefit received status during this period. Most often, the work done count in a period does not change even if you view it later since it tracks transactions done in that period. However, if we abort applications during internal reviews, those applications will not be counted in work done during the period and hence counts may change."
    },
    "Date Range": {
      title: "Date Range",
      description: "The date range filter allows you to select a specific period to analyze work done. Both the start date and end date are inclusive in the query.",
      example: "If you select 1/Jan/2026 - 31/Mar/2026, the data will include work done on 1st January AND 31st March as well."
    },
    "Total Work Done": {
      title: "Total Work Done",
      description: "Total Work Done represents the cumulative actual activities completed on ground during the selected period. This includes all applications that were opened, submitted, received benefits, aborted, and rejected during this time period. These are the raw, unfiltered counts of all work completed.",
      example: "Total Work Done includes:\n• Applications Opened: 100\n• Applications Submitted: 50\n• Applications with Benefit Received: 40\n• Applications Aborted: 7\n• Applications Rejected: 3"
    },
    "Net Values": {
      title: "Net Values",
      description: "Net Values represent the total applications after deducting aborted and rejected applications. This gives you the 'clean' count of applications that are valid and progressing through the system.",
      example: "Net Applications Opened = Total Applications Opened - (Applications Aborted + Applications Rejected)\nExample: 90 = 100 - (7 + 3)\n\nNet Applications Submitted = Total Applications Submitted - Applications Rejected\nExample: 47 = 50 - 3\n\nNet Applications with Benefit Received = Applications with Benefit Received (after reversals)"
    },
    "Applications Opened": {
      title: "Applications Opened",
      description: "This shows the total count of applications that were opened during the selected period."
    },
    "Applications Submitted": {
      title: "Applications Submitted",
      description: "This shows the total count of applications in 'Submitted' status during the selected period. These are applications where the proof of submission has been uploaded."
    },
    "Applications with Benefit Received": {
      title: "Applications with Benefit Received",
      description: "This shows the total count of applications in 'Benefit Received' status during the selected period. These are applications where the proof of benefit has been uploaded on the app."
    },
    "Applications Aborted": {
      title: "Applications Aborted",
      description: "These are applications that were started by HD but aborted either due to incorrect internal records or during internal quality checks during the selected period."
    },
    "Applications Rejected": {
      title: "Applications Rejected",
      description: "These are applications that were rejected by the government during the selected period."
    },
    "Net Applications Opened": {
      title: "Net Applications Opened",
      description: "Net applications opened represents the total valid applications opened after deducting aborted and rejected applications.",
      example: "Net Applications Opened = Total Applications Opened - (Applications Aborted + Applications Rejected)\nExample: 90 = 100 - (7 + 3)"
    },
    "Net Applications Submitted": {
      title: "Net Applications Submitted",
      description: "Net applications submitted represents the total applications submitted after deducting those that were rejected.",
      example: "Net Applications Submitted = Total Applications Submitted - Applications Rejected\nExample: 47 = 50 - 3"
    },
    "Net Applications with Benefit Received": {
      title: "Net Applications with Benefit Received",
      description: "Net applications with benefit received represents the total applications that successfully received benefits after deducting any that were reversed or invalidated."
    },
    "Org/Workspace": {
      title: "Organization/Workspace",
      description: "Choose the Org or Workspace name, which corresponds to the state or workspace associated with the selected Project.",
      example: "Example: Maharashtra, Karnataka, Delhi NCR, etc."
    },
    "Service Type": {
      title: "Service Type",
      description: "Choose the service type associated with the selected Project to filter the data",
      example: "Example: Schemes, Document, DFL, Yojana Card, EDP"
    },
    "Service Name": {
      title: "Service Name",
      description: "Choose the Service Name. A filter used to display services associated with the selected Project to filter the data.",
      example: "Example: PM Awas Yojana, Ayushman Bharat, PM Kisan Samman Nidhi, etc."
    },
    "Service District": {
      title: "Service District",
      description: "Filter data by the district where services are being provided. This helps in analyzing location-specific performance.",
      example: "Example: Gurugram, Bangalore, Delhi, Chennai, etc."
    }
  };

  const mergeOptions = (selected, fetched) => {
    const map = new Map();
    [...selected, ...fetched].forEach(opt => map.set(opt.value, opt));
    return Array.from(map.values());
  };

  const getQueryParams = () => {

    const params = new URLSearchParams();

    appliedFilters.schemeTypes.forEach(type => params.append("SchemeType", type));
    appliedFilters.orgs.forEach(org => params.append("OrgName", org));
    appliedFilters.schemes.forEach(scheme => params.append("SchemeName", scheme));
    appliedFilters.caseDistricts.forEach(d => params.append("CaseDistrict", d));

    if (pid) params.append("pid", pid);
    if (appliedFilters.startDate) params.append("start_date", appliedFilters.startDate);
    if (appliedFilters.endDate) params.append("end_date", appliedFilters.endDate);

    return params.toString();
  };

  const fetchFilterOptions = async () => {

    try {

      const params = getQueryParams();

      const [orgRes, schemeRes, schemeTypeRes, districtRes] = await Promise.all([
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-org/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme-type/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-case-districts/?${params}`)
      ]);

      const orgData = await orgRes.json();
      const schemeData = await schemeRes.json();
      const schemeTypeData = await schemeTypeRes.json();
      const districtData = await districtRes.json();

      const orgOpts = orgData.unique_Organizations.map(o => ({ value: o, label: o }));
      const schemeOpts = schemeData.unique_Schemes.map(s => ({ value: s, label: s }));

      const schemeTypeOpts = schemeTypeData.unique_Scheme_Types.map(t => ({
        value: t,
        label: schemeTypeLabelMap[t] || t
      }));

      const districtOpts = districtData.unique_case_districts.map(d => ({
        value: d,
        label: d
      }));

      setOrgOptions(prev => mergeOptions(prev, orgOpts));
      setSchemeOptions(prev => mergeOptions(prev, schemeOpts));
      setSchemeTypeOptions(prev => mergeOptions(prev, schemeTypeOpts));
      setCaseDistrictOptions(prev => mergeOptions(prev, districtOpts));

    } catch (error) {

      console.error('Error fetching filter options:', error);

    }
  };

  const fetchChartData = async () => {

    setLoading(true);

    try {

      const params = getQueryParams();

      const isDateFilterApplied =
        appliedFilters.startDate || appliedFilters.endDate;

      const chartApi = isDateFilterApplied
        ? `https://api-project360-insights.haqdarshak.com/kpi/funnel/chart/?${params}`
        : `https://api-project360-insights.haqdarshak.com/kpi/waterfall/chart/?${params}`;

      const chartRes = await fetch(chartApi);
      const data = await chartRes.json();

      const netData = [
        { name: "Applications Opened", value: data.application_open || 0 },
        { name: "Applications Submitted", value: data.docket_submitted || 0 },
        { name: "Applications with Benefit Received", value: data.br_count || 0 }
      ];

      setNetTiles(netData);

      // ALWAYS call tiles API
      const tilesRes = await fetch(
        `https://api-project360-insights.haqdarshak.com/kpi/funnel/chart/tiles/?${params}`
      );

      const tilesData = await tilesRes.json();

      const actualData = [
        {
          name: "Applications Opened",
          value: tilesData.application_open
        },
        {
          name: "Applications Submitted",
          value: tilesData.docket_submitted
        },
        {
          name: "Applications with Benefit Received",
          value: tilesData.br_count
        },
        {
          name: "Applications Aborted",
          value: data.aborted_count
        },
        {
          name: "Applications Rejected",
          value: data.rejected_count
        }
      ];

      setActualTiles(actualData);

    } catch (error) {

      console.error("Error fetching chart data:", error);

    } finally {

      setLoading(false);

    }

  };

  useEffect(() => {

    fetchFilterOptions();
    fetchChartData();

  }, [appliedFilters, pid]);

  const applyFilters = () => {

    setAppliedFilters({
      orgs: selectedOrgs,
      schemes: selectedSchemes,
      schemeTypes: selectedSchemeTypes,
      caseDistricts: selectedCaseDistrict,
      startDate,
      endDate
    });

  };

  const clearFilters = () => {

    setSelectedOrgs([]);
    setSelectedSchemes([]);
    setSelectedSchemeTypes([]);
    setSelectedCaseDistrict([]);

    setStartDate('');
    setEndDate('');

    setAppliedFilters({
      orgs: [],
      schemes: [],
      schemeTypes: [],
      caseDistricts: [],
      startDate: '',
      endDate: ''
    });

  };

  // Info Button Component
  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />

        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  const MultiSelectDropdown = ({ options, selected, setSelected, placeholder, label }) => (
    <div className="relative">
      <div className="flex items-center mb-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <InfoButton title={label} />
      </div>
      <Select
        isMulti
        options={options}
        value={options.filter(opt => selected.includes(opt.value))}
        onChange={(selectedOptions) => setSelected(selectedOptions.map(o => o.value))}
        placeholder={placeholder}
        closeMenuOnSelect={false}
        className="text-sm"
        styles={{
          control: (base) => ({
            ...base,
            borderColor: '#e2e8f0',
            '&:hover': { borderColor: '#94a3b8' },
            boxShadow: 'none',
            '&:focus-within': {
              borderColor: '#3b82f6',
              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
            }
          })
        }}
      />
    </div>
  );

  // Info Popup Component
  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]" onClick={closeInfoPopup}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Close info popup
  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  return (

    <div className="max-w-[90vw] ml-[3vw] mt-[3vh] border border-gray-200 shadow-lg rounded-xl bg-white">

      {/* Header */}
      <div className="px-6 py-4 rounded-t-xl border-b border-gray-200">
        <div className="flex items-center">
          <h2 className="text-xl font-bold text-pink-800">Work Done in Period</h2>
          <InfoButton title="Work Done in Period" />
        </div>
        <p className="text-sm text-pink-600 mt-1">
          Track actual work completed on ground during the selected period
        </p>
      </div>

      {/* Filters Section */}
      <div className="px-6 py-5 bg-white border-b border-gray-200">
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          
          <MultiSelectDropdown 
            options={orgOptions} 
            selected={selectedOrgs} 
            setSelected={setSelectedOrgs} 
            placeholder="Select org/workspace"
            label="Org/Workspace"
          />
          
          <MultiSelectDropdown 
            options={caseDistrictOptions} 
            selected={selectedCaseDistrict} 
            setSelected={setSelectedCaseDistrict} 
            placeholder="Select district..."
            label="Service District"
          />

          <MultiSelectDropdown 
            options={schemeTypeOptions} 
            selected={selectedSchemeTypes} 
            setSelected={setSelectedSchemeTypes} 
            placeholder="Select service type..."
            label="Service Type"
          />
          
          <MultiSelectDropdown 
            options={schemeOptions} 
            selected={selectedSchemes} 
            setSelected={setSelectedSchemes} 
            placeholder="Select service name..."
            label="Service Name"
          />

          <div>
            <div className="flex items-center mb-1">
              <label className="text-sm font-medium text-gray-700">Date Range</label>
              <InfoButton title="Date Range" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input 
                type="date" 
                value={startDate} 
                onChange={e => setStartDate(e.target.value)} 
                className="border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent text-sm w-full"
                placeholder="Start Date"
              />
              <input 
                type="date" 
                value={endDate} 
                min={startDate} 
                onChange={e => setEndDate(e.target.value)} 
                className="border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent text-sm w-full"
                placeholder="End Date"
              />
            </div>
          </div>

        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
          <button 
            onClick={applyFilters} 
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
          >
            Apply Filters
          </button>
          <button 
            onClick={clearFilters} 
            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-all duration-200"
          >
            Clear Filters
          </button>
        </div>

      </div>

      {/* Data Section */}
      <div className="p-6 bg-gray-50/30">

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-pink-200 border-t-pink-600 rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-gray-500">Loading data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Total Work Done */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-pink-700">Total Work Done</h3>
                <InfoButton title="Total Work Done" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {actualTiles.map((item, i) => (
                  <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-gray-600">{item.name}</p>
                      <InfoButton title={item.name} />
                    </div>
                    <p className="text-2xl font-bold" style={{ color: '#CF87C4' }}>
                      {item.value?.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Net Values */}
            <div>
              <div className="flex items-center mb-4">
                <h3 className="text-lg font-semibold text-pink-700">Net Values</h3>
                <InfoButton title="Net Values" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {netTiles.map((item, i) => (
                  <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-gray-600">{item.name}</p>
                      <InfoButton title={item.name} />
                    </div>
                    <p className="text-2xl font-bold" style={{ color: '#CF87C4' }}>
                      {item.value?.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </div>

      {/* Info Popup */}
      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>

    </div>

  );

};

export default FunnelChartComponent;